/**
 * Campaign colors (TinyCMS UX optimization review, 2026-10-24): every dated
 * campaign has one, picked on its page, and it shades the campaign's bar on
 * Today and in Campaigns › Calendar, with a small square by its name so a
 * name matches its bar. Everyone on the site sees the same color
 * (projects.color). The bar still shows the status: solid while running or
 * scheduled, stripes while waiting on approval, a dashed outline as a draft,
 * a faded shade once ended, a grey dashed outline if never approved.
 *
 * The values are the --ink-c-<key> tokens (ink.css), with a lighter version
 * in dark mode; --ink-c-<key>-ink is the text on a solid bar.
 */

/** In the picker's order: [key, name]. Keys are what projects.color stores. */
export const CAMPAIGN_COLORS = [
  ['rose', 'Pink'],
  ['orange', 'Tangerine'],
  ['ochre', 'Yellow'],
  ['green', 'Lime'],
  ['teal', 'Aqua'],
  ['blue', 'Cobalt'],
  ['violet', 'Purple'],
  ['slate', 'Ink'],
] as const;

export type CampaignHue = (typeof CAMPAIGN_COLORS)[number][0];

const KEYS = new Set<string>(CAMPAIGN_COLORS.map(([k]) => k));

export const isCampaignHue = (v: unknown): v is CampaignHue => typeof v === 'string' && KEYS.has(v);

export const campaignColorName = (hue: CampaignHue) =>
  (CAMPAIGN_COLORS.find(([k]) => k === hue) || CAMPAIGN_COLORS[0])[1];

/**
 * The campaign's color: what was picked, or (a campaign from before colors,
 * until someone picks one) one spread by its id, the same on every screen.
 */
export function campaignHue(c: { id: string; color?: string | null }): CampaignHue {
  if (isCampaignHue(c.color)) return c.color;
  let h = 0;
  for (const ch of String(c.id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CAMPAIGN_COLORS[h % CAMPAIGN_COLORS.length][0];
}

export const hueVar = (hue: CampaignHue) => `var(--ink-c-${hue})`;

/** What the bar shows about the campaign's status. */
export type BarState = 'solid' | 'waiting' | 'draft' | 'ended' | 'expired';

export interface CampaignPaint {
  /** The bar's background (a color, or stripes). */
  background: string;
  border: string;
  /** The dates' color on the bar. */
  ink: string;
  /** The square by the campaign's name. */
  chip: string;
}

export function campaignPaint(hue: CampaignHue, state: BarState): CampaignPaint {
  const c = hueVar(hue);
  const mix = (n: number) => `color-mix(in oklch, ${c} ${n}%, transparent)`;
  if (state === 'waiting')
    return {
      // Solid under the dates, then stripes in the color.
      background: `linear-gradient(90deg, ${c} 0 100px, transparent 132px), repeating-linear-gradient(135deg, ${c} 0 6px, ${mix(55)} 6px 12px)`,
      border: `1px solid ${c}`,
      ink: `var(--ink-c-${hue}-ink)`,
      chip: c,
    };
  // The dashes a shade darker than the hue: a light one (Yellow, Lime) on its
  // own tint read as a faint solid edge (QA round 23).
  if (state === 'draft')
    return {
      background: mix(22),
      border: `1.5px dashed color-mix(in oklch, ${c}, var(--ink-ink) 35%)`,
      ink: 'var(--ink-ink)',
      chip: c,
    };
  if (state === 'ended')
    return { background: mix(45), border: `1px solid ${mix(70)}`, ink: 'var(--ink-ink)', chip: c };
  if (state === 'expired')
    return {
      background: 'transparent',
      border: '1.5px dashed var(--ink-line3)',
      ink: 'var(--ink-ink3)',
      chip: c,
    };
  return { background: c, border: `1px solid ${c}`, ink: `var(--ink-c-${hue}-ink)`, chip: c };
}
